import { useState, useEffect } from "react";

export default function AddPlayerForm({ API, accessToken, selectedTeamId }) {
  const [players, setPlayers] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState("");

  // State for editing player
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editedName, setEditedName] = useState("");

  // Fetch all players whenever the selected team changes
  useEffect(() => {
    if (!selectedTeamId) return;

    const fetchPlayers = async () => {
      try {
        const res = await fetch(`${API}/players`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        setPlayers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch players:", err);
      }
    };

    fetchPlayers();
  }, [API, accessToken, selectedTeamId]);

  // Add a new player
  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (!newPlayerName.trim() || !selectedTeamId) return;

    try {
      const res = await fetch(`${API}/players`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          player_name: newPlayerName.trim(),
          team_id: selectedTeamId,
        }),
      });

      if (!res.ok) throw new Error("Failed to add player");

      setNewPlayerName("");

      // Refresh player list
      const updated = await fetch(`${API}/players`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }).then((r) => r.json());

      setPlayers(Array.isArray(updated) ? updated : []);
    } catch (err) {
      console.error(err);
    }
  };

  // Save edited player name
  const savePlayerName = async (playerId) => {
    if (!editedName.trim()) return;

    try {
      const res = await fetch(`${API}/players/${playerId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ player_name: editedName.trim() }),
      });

      if (!res.ok) throw new Error("Failed to update player");

      const updatedPlayer = await res.json();

      setPlayers((prev) =>
        prev.map((p) => (p.id === playerId ? updatedPlayer : p))
      );

      setEditingPlayerId(null);
      setEditedName("");
    } catch (err) {
      console.error(err);
      alert("Failed to update player name");
    }
  };

  // Filter players for the selected team
  const teamPlayers = players.filter(
    (p) => Number(p.team_id) === Number(selectedTeamId)
  );

  if (!selectedTeamId) return null;

  return (
    <div className="max-w-lg mx-auto mb-10">
      <h2 className="text-lg font-bold mb-2">Add Player</h2>

      <form
        onSubmit={handleAddPlayer}
        className="flex flex-col gap-3 bg-gray-100 p-4 rounded shadow"
      >
        <input
          type="text"
          placeholder="Player Name"
          value={newPlayerName}
          onChange={(e) => setNewPlayerName(e.target.value)}
          className="w-full border p-2 rounded"
          required
        />

        <div className="flex justify-end">
          <button className="bg-blue-500 px-4 py-2 text-black rounded">
            Add Player
          </button>
        </div>
      </form>

      {/* Existing players for this team */}
      {teamPlayers.length > 0 && (
        <div className="mt-4">
          <h3 className="font-semibold">Existing Players:</h3>
          <ul className="space-y-2">
            {teamPlayers.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-4">
                {editingPlayerId === p.id ? (
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="border p-1 rounded text-sm"
                  />
                ) : (
                  <span>{p.player_name}</span>
                )}

                <div className="ml-4 flex gap-2">
                  {editingPlayerId === p.id ? (
                    <>
                      <button
                        className="bg-green-500 text-black px-2 py-1 rounded text-sm"
                        onClick={() => savePlayerName(p.id)}
                      >
                        Save
                      </button>
                      <button
                        className="bg-gray-300 text-black px-2 py-1 rounded text-sm"
                        onClick={() => setEditingPlayerId(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                        className="
                        bg-yellow-400 text-black
                        px-3 py-1.5
                        rounded-md
                        text-sm font-medium
                        shadow-sm
                        hover:bg-yellow-500
                        focus:outline-none focus:ring-2 focus:ring-yellow-300
                      "
                      onClick={() => {
                        setEditingPlayerId(p.id);
                        setEditedName(p.player_name);
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
